import { EventType } from "@/analytics/analytics-events";
import {
  SelectColoredAnalysisPlaneFitMethodEventProperties,
  ToggleAnalysisReferencePlaneVisibilityProperties,
} from "@/analytics/cloud-analysis-events-properties";
import {
  getDistanceUnitSymbolText,
  isMetric,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { DistanceInputField } from "@/components/ui/distance-input-field";
import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { PointCloudObject } from "@/object-cache";
import {
  PointCloudAnalysis,
  ReferencePlaneType,
  setAnalysisElevation,
  setAnalysisReferencePlaneType,
  setAnalysisShowReferencePlane,
  setAnalysisVolumeBasePlaneElevation,
  setAnalysisVolumeBasePlaneType,
  VolumeAnalysisBasePlaneType,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { computeVolumeAnalysis } from "@/utils/colormap-analysis-utils";
import {
  Dropdown,
  FaroIconButton,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { DistanceUnitTypes, validateEnumValue } from "@faro-lotv/foundation";
import { Stack } from "@mui/material";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";

type ColormapReferencePlaneProps = {
  /** The point cloud object used by the analysis */
  pointCloud: PointCloudObject;

  /** Active analysis object to be modified. */
  analysis: PointCloudAnalysis;

  /** Width of the panel */
  width?: CSSProperties["width"];
};

const planeOptions = [
  { key: "flatness", label: "Flatness", value: ReferencePlaneType.flatness },
  {
    key: "levelness",
    label: "Level",
    value: ReferencePlaneType.levelness,
  },
  {
    key: "elevation",
    label: "Elevation",
    value: ReferencePlaneType.elevation,
  },
  { key: "plumbness", label: "Plumb", value: ReferencePlaneType.plumbness },
  { key: "volume", label: "Volume", value: ReferencePlaneType.volume },
];

const volumeBasePlaneOptions = [
  {
    key: "bestFitPolygonBase",
    label: "Best-fit plane",
    value: VolumeAnalysisBasePlaneType.bestFitPolygonBase,
  },
  {
    key: "levelLowestPointBase",
    label: "Level - lowest point",
    value: VolumeAnalysisBasePlaneType.levelLowestPointBase,
  },
  {
    key: "levelHighestPointBase",
    label: "Level - highest point",
    value: VolumeAnalysisBasePlaneType.levelHighestPointBase,
  },
  {
    key: "levelAtElevation",
    label: "Level - elevation",
    value: VolumeAnalysisBasePlaneType.levelAtElevation,
  },
];

/**
 *
 * Returns the label of the analysis type corresponding to the given ReferencePlaneType value.
 *
 * @param value - The ReferencePlaneType value to look up.
 * @returns The label string if found, otherwise undefined.
 */
export function getAnalysisTypeByValue(
  value: ReferencePlaneType,
): string | undefined {
  return planeOptions.find((option) => option.value === value)?.label;
}

function referencePlaneTypeFromString(
  value: string,
): ReferencePlaneType | undefined {
  return validateEnumValue(value, ReferencePlaneType) ? value : undefined;
}

function volumeBasePlaneTypeFromString(
  value: string,
): VolumeAnalysisBasePlaneType | undefined {
  return validateEnumValue(value, VolumeAnalysisBasePlaneType)
    ? value
    : undefined;
}

/** @returns Colormap reference plane UI panel */
export function ColormapReferencePlanePanel({
  pointCloud,
  analysis,
  width,
}: ColormapReferencePlaneProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [planeOption, setPlaneOption] = useState<string>(
    analysis.referencePlaneType,
  );

  const changePlaneOption = useCallback(
    async (newPlaneOption: string) => {
      Analytics.track<SelectColoredAnalysisPlaneFitMethodEventProperties>(
        EventType.selectColoredAnalysisPlaneFitMethod,
        {
          newValue: newPlaneOption,
        },
      );

      setPlaneOption(newPlaneOption);
      const referencePlaneType = referencePlaneTypeFromString(newPlaneOption);
      if (
        referencePlaneType &&
        referencePlaneType !== analysis.referencePlaneType
      ) {
        dispatch(
          setAnalysisReferencePlaneType({
            analysisId: analysis.id,
            referencePlaneType,
          }),
        );
        if (
          referencePlaneType === ReferencePlaneType.volume &&
          !analysis.volumeAnalysisResult
        ) {
          await computeVolumeAnalysis(
            pointCloud,
            analysis,
            analysis.volumeBasePlaneType ??
              VolumeAnalysisBasePlaneType.bestFitPolygonBase,
            dispatch,
          );
        }
      }
    },
    [analysis, dispatch, pointCloud],
  );

  const [volumeBasePlaneOption, setVolumeBasePlaneOption] = useState<string>(
    analysis.volumeBasePlaneType ??
      VolumeAnalysisBasePlaneType.bestFitPolygonBase,
  );

  const changeVolumeBasePlaneOption = useCallback(
    async (newVolumeBasePlaneOption: string) => {
      setVolumeBasePlaneOption(newVolumeBasePlaneOption);
      const volumeBasePlaneType = volumeBasePlaneTypeFromString(
        newVolumeBasePlaneOption,
      );
      if (
        volumeBasePlaneType &&
        volumeBasePlaneType !== analysis.volumeBasePlaneType
      ) {
        dispatch(
          setAnalysisVolumeBasePlaneType({
            analysisId: analysis.id,
            volumeBasePlaneType,
          }),
        );
        // only need recompute when switching between bestFit and level options
        // all level options use the same volume direction, no need to recompute
        if (
          volumeBasePlaneType ===
            VolumeAnalysisBasePlaneType.bestFitPolygonBase ||
          analysis.volumeBasePlaneType ===
            VolumeAnalysisBasePlaneType.bestFitPolygonBase
        ) {
          await computeVolumeAnalysis(
            pointCloud,
            analysis,
            volumeBasePlaneType,
            dispatch,
          );
        }
      }
    },
    [analysis, dispatch, pointCloud],
  );

  const distanceUnit = useDistanceUnit();

  const elevationUnit = useMemo(() => {
    if (distanceUnit === DistanceUnitTypes.decimalUSSurveyFeet) {
      return DistanceUnitTypes.decimalUSSurveyFeet;
    }

    return isMetric(distanceUnit)
      ? DistanceUnitTypes.meters
      : DistanceUnitTypes.decimalFeet;
  }, [distanceUnit]);

  const [showReferencePlaneChecked, setShowReferencePlaneChecked] =
    useState(false);
  useEffect(() => {
    setShowReferencePlaneChecked(analysis.showReferencePlane ?? false);
  }, [analysis.showReferencePlane]);
  const toggleShowReferencePlane = useCallback(
    (showReferencePlane: boolean) => {
      setShowReferencePlaneChecked(showReferencePlane);
      dispatch(
        setAnalysisShowReferencePlane({
          analysisId: analysis.id,
          showReferencePlane,
        }),
      );
    },
    [analysis.id, dispatch],
  );

  return (
    <Stack spacing={1} sx={{ width }}>
      <Dropdown
        variant="underlined"
        options={planeOptions}
        value={planeOption}
        dark
        onChange={(e) => changePlaneOption(e.target.value)}
        shouldCapitalize={false}
      />

      {planeOption === ReferencePlaneType.volume && (
        <>
          <Dropdown
            variant="underlined"
            options={volumeBasePlaneOptions}
            value={volumeBasePlaneOption}
            dark
            onChange={(e) => changeVolumeBasePlaneOption(e.target.value)}
            shouldCapitalize={false}
          />
          {analysis.volumeBasePlaneType ===
            VolumeAnalysisBasePlaneType.levelAtElevation &&
            analysis.volumeBasePlaneElevation !== undefined && (
              <ElevationInputField
                elevation={analysis.volumeBasePlaneElevation}
                elevationUnit={elevationUnit}
                onElevationChanged={(elevation) => {
                  if (elevation !== undefined) {
                    dispatch(
                      setAnalysisVolumeBasePlaneElevation({
                        analysisId: analysis.id,
                        elevation,
                      }),
                    );
                  }
                }}
              />
            )}
        </>
      )}

      {planeOption === ReferencePlaneType.elevation && (
        <ElevationInputField
          elevation={analysis.elevation ?? 0}
          elevationUnit={elevationUnit}
          onElevationChanged={(elevation) => {
            if (elevation !== undefined) {
              dispatch(
                setAnalysisElevation({ analysisId: analysis.id, elevation }),
              );
            }
          }}
        />
      )}

      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <FaroText
          variant="labelL"
          sx={{
            color: grey[0],
            width: "80%",
          }}
        >
          Reference plane
        </FaroText>

        <FaroIconButton
          onClick={() => {
            Analytics.track<ToggleAnalysisReferencePlaneVisibilityProperties>(
              EventType.toggleVisibilityOfColoredAnalysisReferencePlane,
              {
                visible: !showReferencePlaneChecked,
              },
            );
            toggleShowReferencePlane(!showReferencePlaneChecked);
          }}
          margin={0.5}
          size="s"
          aria-label="toggle reference plane visibility"
          color={grey[0]}
          hoverColor={grey[0]}
        >
          <ElementIcon
            icon={
              showReferencePlaneChecked
                ? ElementIconType.Visible
                : ElementIconType.NonVisible
            }
          />
        </FaroIconButton>
      </Stack>
    </Stack>
  );
}

type ElevationInputFieldProps = {
  elevation: number;
  elevationUnit: DistanceUnitTypes;
  onElevationChanged(elevation: number | undefined): void;
};

function ElevationInputField({
  elevation,
  elevationUnit,
  onElevationChanged,
}: ElevationInputFieldProps): JSX.Element {
  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      <FaroText
        variant="labelL"
        sx={{
          color: grey[0],
        }}
      >
        Elevation:
      </FaroText>
      <DistanceInputField
        variant="underlined"
        fullWidth
        dark
        distanceInMeter={elevation}
        distanceUnit={elevationUnit}
        errorMessage="Invalid number"
        onDistanceChanged={onElevationChanged}
        inputProps={{ style: { textAlign: "right" } }}
      />
      <FaroText
        variant="labelL"
        className={NO_TRANSLATE_CLASS}
        sx={{
          color: grey[0],
        }}
      >
        {getDistanceUnitSymbolText(elevationUnit)}
      </FaroText>
    </Stack>
  );
}
